import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  InputField,
  RadioGroupField,
  CheckboxField,
  useRepeatedSection
} from '@trp-beacon-ui-kit/react-controls';
import { ToolTip } from '@trp-beacon-ui-kit/react-communication';
import { FlowContext } from '../../core/flow-engine';

import styles from './style.css';
import alertIcon from '../../images/icon_exclaimation.svg';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import Heading from '../Typography/Heading/Heading';
import FundQuickSearch from '../FundQuickSearch/FundQuickSearch';
import { transformIntoQuickSearchFunds } from './utils';
import { isEmpty } from '../../core/util';
import {
  accountNameValidation,
  validateTransferAmount
} from '../../flows/shared/validations';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';
import { RewardsContext } from '../../context/RewardsContext/RewardsContext';
import warningIcon from '../../images/icon-warning.svg';

const answeredNo = question => question === false;
/* eslint-disable complexity */

export const MessageIcon = () => {
  const text =
    'To complete the transfer, you must liquidate your assets after submitting this application. The term cash featured as the fund name serves as a placeholder only.';

  return (
    <div className="trp-message">
      <div className="icon">
        <img alt="iconImg" height="40px" src={alertIcon} />
      </div>
      <div className="alert-content">
        <span>{text}</span>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

export const RenderCashBox = ({ preliquidated, handleAmountFieldChange }) => {
  const {
    formProps: { accountName, accountNumber, transferBalance, transferAmount },
    appState: {
      store: {
        app: { externalAccounts }
      }
    }
  } = useContext(FlowContext);

  const defaults = {
    funds: (externalAccounts || []).map(account => ({
      [accountName.name]: account.accountName,
      [accountNumber.name]: account.accountNumber,
      [transferAmount.name]: account.estimatedTransferAmount,
      [transferBalance.name]: account.liquidationOption
    }))
  };

  const { sections } = useRepeatedSection('funds', defaults);
  return (
    <div className="sections">
      {answeredNo(preliquidated) && <MessageIcon />}

      {sections.map(({ key, getDefault, makeName }, idx) => {
        const transferBalanceDefault = getDefault(transferBalance.name);
        return (
          idx < 1 && (
            <div key={key} className="section">
              <div className="transfer-account-fields">
                <div className="account-name-input">
                  <InputField
                    title="Account Name"
                    value="Cash"
                    {...accountName}
                    name={makeName(accountName.name)}
                    defaultValue={getDefault(accountName.name)}
                  />
                </div>
                <div className="account-number-input">
                  <InputField
                    title="Account Number"
                    {...accountNumber}
                    name={makeName(accountNumber.name)}
                    defaultValue={getDefault(accountNumber.name)}
                    maxLength="30"
                    className="blocked_data"
                  />
                </div>
              </div>

              <div className="transfer-amount-fields">
                <div className="transfer-type-input">
                  <RadioGroupField
                    title="Transfer Balance"
                    options={[
                      {
                        title: 'Full',
                        value: 'ALL',
                        defaultChecked: transferBalanceDefault === 'ALL'
                      },
                      {
                        title: 'Partial',
                        value: 'PARTIAL',
                        defaultChecked: transferBalanceDefault === 'PARTIAL'
                      }
                    ]}
                    horizontal
                    {...transferBalance}
                    name={makeName(transferBalance.name)}
                  />
                </div>
                <div className="transfer-amount-input">
                  <InputField
                    type="number"
                    title="Estimated Transfer Amount"
                    {...transferAmount}
                    name={makeName(transferAmount.name)}
                    defaultValue={getDefault(transferAmount.name)}
                    onBlur={handleAmountFieldChange}
                  />
                </div>
              </div>
            </div>
          )
        );
      })}
      <style jsx>{styles}</style>
    </div>
  );
};

export const RenderBox = ({
  cusips,
  setCusips,
  setErrorFlag,
  accountNames,
  setAccountNames,
  handleAmountFieldChange
}) => {
  const {
    formProps: {
      accountName,
      accountNumber,
      allAccountsSameNumber,
      transferBalance,
      transferAmount
    },
    appState: {
      store: {
        loadedData: { morningstarFamilyFunds = [] },
        app: { externalAccounts = [] }
      }
    }
  } = useContext(FlowContext);

  const [manualEntry, setManualEntry] = useState(false);

  const [touched, toggleTouched] = useState([]);

  const fundQuickSearchFunds =
    transformIntoQuickSearchFunds(morningstarFamilyFunds) || [];

  const updateAccountNames = (idx, value) => {
    const updatedAccountNames = [...accountNames];
    updatedAccountNames[idx] = value;
    setAccountNames(updatedAccountNames);
  };

  const handleInputBehavior = (idx, value) => {
    const newTouchedValue = [...touched];
    newTouchedValue[idx] = true;
    toggleTouched(newTouchedValue);
    if (value === 'none') {
      setManualEntry(true);
    } else {
      setManualEntry(false);
      updateAccountNames(idx, value);
    }
  };

  /* function handler to update account names array
   when a section is removed */
  const handleRemoveSection = idx => {
    const filteredAccountNames = accountNames.filter(
      (_, index) => index !== idx
    );
    setAccountNames(filteredAccountNames);
  };

  const defaults = {
    funds: (externalAccounts || []).map(account => ({
      [accountName.name]: account.accountName,
      [accountNumber.name]: account.accountNumber,
      [transferAmount.name]: account.estimatedTransferAmount,
      [transferBalance.name]: account.liquidationOption
    }))
  };

  const { sections, addSection } = useRepeatedSection('funds', defaults);
  const sectionsRef = useRef(null);
  const [sameAccountNumber, toggleSameAccountNumber] = useState(
    allAccountsSameNumber?.defaultChecked
  );

  const applyAccountNumberToOtherSections = sameNumber => {
    // Check if checking or unchecking the checkbox
    if (sameNumber) {
      // Grab account name fields from all the sections
      const accountNumberNodes = [
        ...(sectionsRef?.current?.querySelectorAll(
          'input[name*=accountNumber]'
        ) || [])
      ];

      // Only do anything if the first section has account name
      // and there are multiple sections
      if (accountNumberNodes.length > 1 && accountNumberNodes[0].value) {
        const accountNumberToPopulate = accountNumberNodes[0].value;

        /* eslint-disable  no-param-reassign */
        accountNumberNodes.slice(1).forEach(node => {
          // Modify the value in other sections and dispatch a change event
          // to rerender the screen
          node.value = accountNumberToPopulate;
          node.dispatchEvent(new Event('change'));
        });
        /* eslint-enable  no-param-reassign */
      }
    }
  };

  const addSectionAndCheckAccountNumber = () => {
    addSection();
    setTimeout(() => {
      applyAccountNumberToOtherSections(sameAccountNumber);
    }, 50);
  };

  const toggleCheckbox = () => {
    toggleSameAccountNumber(!sameAccountNumber);
    applyAccountNumberToOtherSections(!sameAccountNumber);
  };

  useEffect(() => {
    if (
      sections.length > 0 &&
      (accountNames.length !== sections.length ||
        accountNames.filter(name => isEmpty(name)).length > 0)
    ) {
      setErrorFlag(true);
    } else {
      setErrorFlag(false);
    }
  }, [sections, accountNames]);

  return (
    <div className="sections" ref={sectionsRef}>
      <div>
        <CheckboxField
          name="allAccountsSameNumber"
          id="checkbox-all-accounts-field"
          checked={sameAccountNumber}
          className="checkbox-all-accounts-field"
          title={
            <>
              {' '}
              All of my accounts have the same account number
              <ToolTip
                id="checkbox-all-accounts-tooltip"
                popOutPlacement="top"
                description="To find your account number, check a recent statement"
              />
            </>
          }
          onClick={toggleCheckbox}
          {...allAccountsSameNumber}
        />
      </div>

      {sections.map(({ key, getDefault, makeName, removeSection }, idx) => {
        const transferBalanceDefault = getDefault(transferBalance.name);
        return (
          <div key={key} className="section">
            {idx > 0 && (
              <div className="remove-section-button">
                <MFBeaconButton
                  noButtonMargin
                  id="removeFundFromTransferBtn"
                  data-name={generateGeneralTestID(
                    'CustodianInstitution',
                    `RemoveFundButton_${idx}`
                  )}
                  type="button"
                  onClick={() => {
                    removeSection();
                    handleRemoveSection(idx);
                  }}
                  variant="tertiary"
                  label="X"
                />
              </div>
            )}
            <div className="transfer-account-fields">
              <div className="account-name-input">
                {manualEntry || !isEmpty(accountNames[idx]) ? (
                  <InputField
                    title="Account Name"
                    {...accountName}
                    name={makeName(accountName.name)}
                    defaultValue={accountNames[idx]}
                    onChange={event =>
                      handleInputBehavior(idx, event.target.value)
                    }
                    validation={accountNameValidation}
                    data-name={generateGeneralTestID(
                      'CustodianInstitution',
                      `AccountName_${idx}`
                    )}
                  />
                ) : (
                  <>
                    <div className="quick-search-label">
                      Search Account Name
                    </div>
                    <FundQuickSearch
                      allMutualFunds={fundQuickSearchFunds}
                      includeIcon={false}
                      mode="MI"
                      flowContextName={makeName(accountName.name)}
                      inputTitle="Account Name"
                      cusipInfo={{
                        idx,
                        fundQuickSearchFunds,
                        cusips,
                        setCusips
                      }}
                      {...accountName}
                      name={makeName(accountName.name)}
                      updateFilterText={text => handleInputBehavior(idx, text)}
                      onBlur={text => handleInputBehavior(idx, text)}
                    />
                    {isEmpty(accountNames[idx]) && touched[idx] && (
                      <div
                        role="alert"
                        aria-atomic="true"
                        className="error-msg">
                        Account Name is Required
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="account-number-input">
                <InputField
                  title="Account Number"
                  {...accountNumber}
                  name={makeName(accountNumber.name)}
                  defaultValue={getDefault(accountNumber.name)}
                  maxLength="30"
                  className="blocked_data"
                  data-name={generateGeneralTestID(
                    'CustodianInstitution',
                    `AccountNumber_${idx}`
                  )}
                />
              </div>
            </div>

            <div className="transfer-amount-fields">
              <div className="transfer-type-input">
                <RadioGroupField
                  title="Transfer Balance"
                  options={[
                    {
                      title: 'Full',
                      value: 'ALL',
                      defaultChecked: transferBalanceDefault === 'ALL'
                    },
                    {
                      title: 'Partial',
                      value: 'PARTIAL',
                      defaultChecked: transferBalanceDefault === 'PARTIAL'
                    }
                  ]}
                  horizontal
                  {...transferBalance}
                  name={makeName(transferBalance.name)}
                />
              </div>
              <div className="transfer-amount-input">
                <InputField
                  type="number"
                  title="Estimated Transfer Amount"
                  {...transferAmount}
                  name={makeName(transferAmount.name)}
                  defaultValue={getDefault(transferAmount.name)}
                  data-name={generateGeneralTestID(
                    'CustodianInstitution',
                    `TransferAmount_${idx}`
                  )}
                  onBlur={handleAmountFieldChange}
                />
              </div>
            </div>
          </div>
        );
      })}
      <MFBeaconButton
        id="addTransferFundBtn"
        data-name={generateGeneralTestID(
          'CustodianInstitution',
          'AddFundButton'
        )}
        type="button"
        onClick={addSectionAndCheckAccountNumber}
        variant="tertiary"
        label="+ Add Another Fund"
      />
      <style jsx>{styles}</style>
    </div>
  );
};

const questionsAnswered = (...boolQuestions) =>
  boolQuestions.every(question => typeof question === 'boolean');

export const AccountInfoInput = ({
  fundsInBrokerage,
  preliquidated,
  cusips,
  setCusips,
  setErrorFlag,
  accountNames,
  setAccountNames,
  handleAmountFieldChange
}) =>
  questionsAnswered(fundsInBrokerage) &&
  (answeredNo(fundsInBrokerage) ? (
    <RenderBox
      cusips={cusips}
      setCusips={setCusips}
      setErrorFlag={setErrorFlag}
      accountNames={accountNames}
      setAccountNames={setAccountNames}
      handleAmountFieldChange={handleAmountFieldChange}
    />
  ) : (
    <RenderCashBox
      preliquidated={preliquidated}
      handleAmountFieldChange={handleAmountFieldChange}
    />
  ));

const ManualInstitution = () => {
  const {
    appState,
    formProps: { assetsInBrokerage, cashAssets },
    next,
    back
  } = useContext(FlowContext);
  const { currentTier } = useContext(RewardsContext) || {};

  const {
    preliquidated: preliquidatedDefault = null,
    fundsInBrokerage: fundsInBrokerageDefault = null,
    app: { externalAccounts = [] },
    chosenInvestments
  } = appState.store;
  const { custodianName } = appState.store.app.custodian;
  const defaultCusips = appState.store.app.externalAccounts
    ? appState.store.app.externalAccounts.map(f => f.cusip)
    : [];
  const [cusips, setCusips] = useState(defaultCusips);
  const [errorFlag, setErrorFlag] = useState(false);
  const [accountNames, setAccountNames] = useState(
    externalAccounts.map(acct => acct.accountName)
  );
  const [transferValidationMessage, setTransferAmountValidation] = useState('');

  const handleAmountFieldChange = event => {
    event.preventDefault();
    const transferAmountValidationMessage = validateTransferAmount({
      chosenInvestments,
      summitTier: currentTier
    });
    setTransferAmountValidation(transferAmountValidationMessage);
  };

  /** RADIO BUTTON STATES START* */
  const [fundsInBrokerage, setFundsInBrokerage] = useState(
    fundsInBrokerageDefault
  );
  const [preliquidated, setPreliquidated] = useState(preliquidatedDefault);

  const fundsInBrokerageHandler = event => {
    const fundsInBrokerageFlag = event.target.value === 'fundsInBrokerageYes';
    setFundsInBrokerage(fundsInBrokerageFlag);
  };

  const preliquidatedHandler = event => {
    const preliquidatedFlag = event.target.value === 'preliquidatedYes';
    setPreliquidated(preliquidatedFlag);
  };
  /** RADIO BUTTON STATES END* */

  return (
    <div className="manual-institution">
      <Heading text="Tell us more about the account you're transferring" />
      <div className="container">
        <div className="assets-title-wrapper">
          <div className="assets-title">
            Are the assets currently in a brokerage account
          </div>

          <div className="funds-in-brokerage">
            <input
              {...assetsInBrokerage}
              id="yes-brokerage"
              data-name={generateGeneralTestID(
                'CustodianInstitution',
                'FundsInBrokerage'
              )}
              name="fundsInBrokerage"
              onChange={fundsInBrokerageHandler}
              type="radio"
              value="fundsInBrokerageYes"
              checked={fundsInBrokerage}
            />
            <label htmlFor="yes-brokerage">Yes</label>

            <input
              {...assetsInBrokerage}
              id="no-brokerage"
              data-name={generateGeneralTestID(
                'CustodianInstitution',
                'FundsNotInBrokerage'
              )}
              name="fundsInBrokerage"
              onChange={fundsInBrokerageHandler}
              type="radio"
              value="fundsInBrokerageNo"
              // using an explicit check here
              // because it has a tri-state
              // where its initial state is null.
              checked={answeredNo(fundsInBrokerage)}
            />
            <label htmlFor="no-brokerage">No</label>
          </div>
        </div>

        {fundsInBrokerage && (
          <>
            <div className="assets-title-wrapper">
              <div className="assets-title">
                Have you converted the assets to cash prior to requesting this
                transfer
              </div>
              <div className="funds-in-brokerage">
                <input
                  {...cashAssets}
                  id="preliquidated"
                  data-name={generateGeneralTestID(
                    'CustodianInstitution',
                    'Preliquidated'
                  )}
                  name="preliquidated"
                  onChange={preliquidatedHandler}
                  type="radio"
                  value="preliquidatedYes"
                  checked={preliquidated}
                />

                <label htmlFor="preliquidated">
                  <span />
                  Yes
                </label>
                <input
                  {...cashAssets}
                  id="no-preliquidated"
                  data-name={generateGeneralTestID(
                    'CustodianInstitution',
                    'NotPreliquidated'
                  )}
                  name="preliquidated"
                  onChange={preliquidatedHandler}
                  type="radio"
                  value="preliquidatedNo"
                  // using an explicit check here
                  // because it has a tri-state
                  // where it's initial state is null.
                  checked={answeredNo(preliquidated)}
                />
                <label htmlFor="no-preliquidated">
                  <span />
                  No
                </label>
              </div>
            </div>
            {questionsAnswered(fundsInBrokerage, preliquidated) && (
              <div className="form-title">
                Enter your {custodianName} mutual funds:
              </div>
            )}
          </>
        )}

        <AccountInfoInput
          fundsInBrokerage={fundsInBrokerage}
          preliquidated={preliquidated}
          cusips={cusips}
          setCusips={setCusips}
          setErrorFlag={setErrorFlag}
          accountNames={accountNames}
          setAccountNames={setAccountNames}
          handleAmountFieldChange={handleAmountFieldChange}
        />
      </div>

      {/* error message for transfer amount iClass validations */}
      {transferValidationMessage && (
        <div role="alert" aria-atomic="true" className="transfer-amount-error">
          <img className="transfer-error-img" src={warningIcon} alt="warning" />
          {transferValidationMessage}
        </div>
      )}

      <div className="button-container">
        <MFBeaconButton
          id="manualInstitutionContinue"
          data-name={generateGeneralTestID(
            'CustodianInstitution',
            'ContinueButton'
          )}
          variant="primary"
          size="normal"
          className="continue-btn"
          disabled={
            !isEmpty(transferValidationMessage) ||
            fundsInBrokerage === null ||
            (answeredNo(fundsInBrokerage) && errorFlag)
          }
          onClick={next({
            fundsInBrokerage,
            preliquidated,
            cusips,
            transferValidationMessage
          })}
          label="Continue"
        />
        <MFBeaconButton
          id="manualInstitutionBack"
          data-name={generateGeneralTestID(
            'CustodianInstitution',
            'BackButton'
          )}
          variant="secondary"
          className="beacon-button-margin-override"
          size="normal"
          onClick={back()}
          label="Back"
        />
      </div>

      <style jsx>{styles}</style>
    </div>
  );
};
/* eslint-enable complexity */

export default ManualInstitution;
